(() => {
  if (window.SopapetsCollectionV4Loaded) return;
  window.SopapetsCollectionV4Loaded = true;

  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const quickViewTriggerSelector = '[data-spv4-quick-view]';
  const collectionScript = document.currentScript;
  const collectionAssetUrl = collectionScript?.src || '';
  const quickViewStylesheetAssetUrl = collectionScript?.dataset.spv4QuickViewStylesUrl || '';
  const prefetchedQuickViewUrls = new Set();

  const ensureQuickViewStylesheet = () => {
    const existing = document.querySelector('link[data-spv4-quick-view-styles]');
    if (existing || (!quickViewStylesheetAssetUrl && !collectionAssetUrl)) return Promise.resolve();

    return new Promise((resolve) => {
      try {
        const stylesheetUrl = new URL(quickViewStylesheetAssetUrl || collectionAssetUrl, window.location.href);
        if (!quickViewStylesheetAssetUrl) {
          stylesheetUrl.pathname = stylesheetUrl.pathname.replace(
            /sopapets-collection-v4\.js$/,
            'sopapets-collection-v4-quick-view.css'
          );
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = stylesheetUrl.href;
        link.setAttribute('data-spv4-quick-view-styles', '');
        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', resolve, { once: true });
        document.head.append(link);
      } catch (_) {
        // Keep Collection V4 functional even if the asset URL cannot be derived.
        resolve();
      }
    });
  };

  void ensureQuickViewStylesheet();

  const prefetchQuickView = (trigger) => {
    const productUrl = trigger?.getAttribute('data-product-url');
    if (!productUrl || prefetchedQuickViewUrls.has(productUrl)) return;

    prefetchedQuickViewUrls.add(productUrl);
    fetch(productUrl, { credentials: 'same-origin', priority: 'low' }).catch(() => {
      prefetchedQuickViewUrls.delete(productUrl);
    });
  };

  const getNavigatorRoots = (scope = document) => {
    const roots = [];
    if (scope?.matches?.('[data-spv4-nav]')) roots.push(scope);
    if (scope?.querySelectorAll) roots.push(...scope.querySelectorAll('[data-spv4-nav]'));
    return roots;
  };

  const updateNavigatorState = (root) => {
    const track = root.querySelector('[data-spv4-nav-track]');
    if (!track) return;

    const prev = root.querySelector('[data-spv4-nav-prev]');
    const next = root.querySelector('[data-spv4-nav-next]');
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const tolerance = 3;

    if (prev) prev.disabled = track.scrollLeft <= tolerance;
    if (next) next.disabled = track.scrollLeft >= maxScroll - tolerance;
  };

  const hydrateNavigator = (root) => {
    if (root.dataset.spv4NavReady === 'true') {
      updateNavigatorState(root);
      return;
    }

    const track = root.querySelector('[data-spv4-nav-track]');
    if (!track) return;

    root.dataset.spv4NavReady = 'true';
    let raf = 0;

    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => updateNavigatorState(root));
    }, { passive: true });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => updateNavigatorState(root));
      observer.observe(track);
      root.spv4ResizeObserver = observer;
    }

    requestAnimationFrame(() => {
      const active = track.querySelector('[aria-current="page"]');
      if (active && track.scrollWidth > track.clientWidth) {
        const desired = active.offsetLeft - (track.clientWidth - active.offsetWidth) / 2;
        track.scrollTo({ left: Math.max(0, desired), behavior: 'auto' });
      }
      updateNavigatorState(root);
    });
  };

  const hydrateNavigators = (scope = document) => {
    getNavigatorRoots(scope).forEach(hydrateNavigator);
  };

  const moveNavigator = (button, direction) => {
    const root = button.closest('[data-spv4-nav]');
    const track = root?.querySelector('[data-spv4-nav-track]');
    if (!track) return;

    const distance = Math.max(160, track.clientWidth * 0.82) * direction;
    track.scrollBy({ left: distance, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  const getQuickViewModal = (trigger) => {
    const selector = trigger.dataset.spv4QuickViewTarget;
    if (!selector || !selector.startsWith('#')) return null;
    return document.querySelector(selector);
  };

  const getGalleryParts = (modal) => {
    const gallery = modal.querySelector('media-gallery');
    const list = modal.querySelector('.product__media-list');
    const slides = list ? Array.from(list.querySelectorAll(':scope > .product__media-item')) : [];
    const stage = list?.closest('slider-component') || list?.parentElement || null;
    return { gallery, list, slides, stage };
  };

  const getSlideMediaId = (slide) => slide?.dataset?.mediaId || '';

  const removeNativeGalleryChrome = (mediaWrapper) => {
    if (!mediaWrapper) return;

    mediaWrapper
      .querySelectorAll('media-gallery .slider-buttons, media-gallery .thumbnail-slider')
      .forEach((element) => element.remove());
  };

  const getGalleryIndex = (slides) => {
    if (slides.length === 0) return 0;

    const v4Index = slides.findIndex((slide) => slide.dataset.spv4Active === 'true');
    if (v4Index >= 0) return v4Index;

    const dawnIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
    return dawnIndex >= 0 ? dawnIndex : 0;
  };

  const keepThumbnailVisible = (rail, thumbnail) => {
    if (!rail || !thumbnail || rail.scrollWidth <= rail.clientWidth) return;

    const thumbStart = thumbnail.offsetLeft;
    const thumbEnd = thumbStart + thumbnail.offsetWidth;
    const visibleStart = rail.scrollLeft;
    const visibleEnd = visibleStart + rail.clientWidth;
    const padding = 8;

    if (thumbStart < visibleStart + padding) {
      rail.scrollTo({ left: Math.max(0, thumbStart - padding), behavior: reduceMotion ? 'auto' : 'smooth' });
    } else if (thumbEnd > visibleEnd - padding) {
      rail.scrollTo({
        left: Math.max(0, thumbEnd - rail.clientWidth + padding),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }
  };

  const updateThumbnailState = (modal, mediaId) => {
    const rail = modal.querySelector('[data-spv4-gallery-thumbnails]');
    if (!rail) return;

    let activeThumbnail = null;
    rail.querySelectorAll('[data-spv4-thumbnail]').forEach((button) => {
      const isActive = button.dataset.spv4ThumbMediaId === mediaId;
      if (isActive) {
        button.setAttribute('aria-current', 'true');
        activeThumbnail = button;
      } else {
        button.removeAttribute('aria-current');
      }
    });

    if (activeThumbnail && rail.dataset.spv4ActiveMediaId !== mediaId) {
      rail.dataset.spv4ActiveMediaId = mediaId;
      keepThumbnailVisible(rail, activeThumbnail);
    }
  };

  function updateGalleryCounter(modal) {
    const { list, slides } = getGalleryParts(modal);
    if (!list || slides.length === 0) return;

    const index = getGalleryIndex(slides);
    const activeMediaId = getSlideMediaId(slides[index]);
    const counter = modal.querySelector('[data-spv4-gallery-counter]');
    const prev = modal.querySelector('[data-spv4-gallery-prev]');
    const next = modal.querySelector('[data-spv4-gallery-next]');
    const digits = Math.max(2, String(slides.length).length);

    if (counter) {
      counter.textContent = `${String(index + 1).padStart(digits, '0')} / ${String(slides.length).padStart(digits, '0')}`;
    }

    [prev, next].forEach((button) => {
      if (button) button.hidden = slides.length < 2;
    });

    if (prev) prev.disabled = slides.length < 2 || index <= 0;
    if (next) next.disabled = slides.length < 2 || index >= slides.length - 1;
    if (activeMediaId) updateThumbnailState(modal, activeMediaId);
  }

  const setGalleryIndex = (modal, targetIndex, { playMedia = true } = {}) => {
    const { gallery, list, slides } = getGalleryParts(modal);
    if (!list || slides.length === 0) return;

    const target = Math.max(0, Math.min(slides.length - 1, targetIndex));
    const targetSlide = slides[target];
    const mediaId = getSlideMediaId(targetSlide);

    slides.forEach((slide, index) => {
      const isActive = index === target;
      slide.dataset.spv4Active = isActive ? 'true' : 'false';
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${index + 1} of ${slides.length}`);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.toggleAttribute('inert', !isActive);
    });

    if (mediaId) list.dataset.spv4ActiveMediaId = mediaId;
    if (playMedia && typeof gallery?.playActiveMedia === 'function') gallery.playActiveMedia(targetSlide);

    updateGalleryCounter(modal);
  };

  const goToGalleryIndex = (modal, targetIndex) => setGalleryIndex(modal, targetIndex);

  const goToGalleryMedia = (modal, mediaId) => {
    const { slides } = getGalleryParts(modal);
    const targetIndex = slides.findIndex((slide) => getSlideMediaId(slide) === mediaId);
    if (targetIndex >= 0) setGalleryIndex(modal, targetIndex);
  };

  const slideGallery = (modal, delta) => {
    const { list, slides } = getGalleryParts(modal);
    if (!list || slides.length < 2) return;

    const current = getGalleryIndex(slides);
    setGalleryIndex(modal, current + delta);
  };

  const createGalleryButton = (direction) => {
    const button = document.createElement('button');
    const isPrev = direction === 'prev';
    button.type = 'button';
    button.className = `spv4-square-action spv4-qv-gallery-button spv4-qv-gallery-button--${direction}`;
    button.setAttribute('aria-label', isPrev ? 'Previous product media' : 'Next product media');
    if (isPrev) button.setAttribute('data-spv4-gallery-prev', '');
    else button.setAttribute('data-spv4-gallery-next', '');
    button.innerHTML = `<span aria-hidden="true">${isPrev ? '←' : '→'}</span>`;
    return button;
  };

  const ensureGalleryChrome = (modal, stage, list, slideCount) => {
    if (!stage) return;
    stage.classList.add('spv4-qv-gallery-stage');

    let counter = modal.querySelector('[data-spv4-gallery-counter]');
    if (!counter) {
      counter = document.createElement('div');
      counter.className = 'spv4-qv-gallery-counter';
      counter.setAttribute('data-spv4-gallery-counter', '');
      counter.setAttribute('aria-live', 'polite');
      counter.setAttribute('aria-atomic', 'true');
    }
    if (counter.parentElement !== stage) stage.append(counter);

    let prev = modal.querySelector('[data-spv4-gallery-prev]');
    let next = modal.querySelector('[data-spv4-gallery-next]');

    if (!prev) {
      prev = createGalleryButton('prev');
      prev.addEventListener('click', () => slideGallery(modal, -1));
    }
    prev.setAttribute('aria-controls', list.id);
    if (prev.parentElement !== stage) stage.append(prev);

    if (!next) {
      next = createGalleryButton('next');
      next.addEventListener('click', () => slideGallery(modal, 1));
    }
    next.setAttribute('aria-controls', list.id);
    if (next.parentElement !== stage) stage.append(next);

    prev.hidden = slideCount < 2;
    next.hidden = slideCount < 2;
  };

  const hydrateGalleryList = (modal, list) => {
    if (!list) return;

    if (!list.id) list.id = `${modal.id}-Gallery`;
    if (list.dataset.spv4GalleryReady === 'true') return;

    list.dataset.spv4GalleryReady = 'true';
    list.setAttribute('tabindex', '0');
    list.setAttribute('role', 'region');
    list.setAttribute('aria-roledescription', 'carousel');
    list.setAttribute('aria-label', 'Product media gallery');

    list.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        slideGallery(modal, -1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        slideGallery(modal, 1);
      }
    });

    let touchStartX = null;
    let touchStartY = null;

    list.addEventListener('touchstart', (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    }, { passive: true });

    list.addEventListener('touchend', (event) => {
      if (touchStartX === null || touchStartY === null) return;
      const touch = event.changedTouches?.[0];
      if (!touch) return;

      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      touchStartX = null;
      touchStartY = null;

      if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      slideGallery(modal, deltaX < 0 ? 1 : -1);
    }, { passive: true });
  };

  const buildThumbnailButton = (modal, slide, index, total) => {
    const mediaId = getSlideMediaId(slide);
    if (!mediaId) return null;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'spv4-qv-thumbnail';
    button.setAttribute('data-spv4-thumbnail', '');
    button.dataset.spv4ThumbMediaId = mediaId;
    button.setAttribute('aria-label', `View product media ${index + 1} of ${total}`);

    const sourceImage = slide.querySelector('.product__media img, .deferred-media__poster img, img');
    if (sourceImage) {
      const image = sourceImage.cloneNode(false);
      image.removeAttribute('id');
      image.removeAttribute('width');
      image.removeAttribute('height');
      image.setAttribute('alt', '');
      image.setAttribute('aria-hidden', 'true');
      image.setAttribute('loading', 'lazy');
      image.setAttribute('sizes', '(max-width: 749px) 56px, 64px');
      button.append(image);
    } else {
      const fallback = document.createElement('span');
      fallback.className = 'spv4-qv-thumbnail__fallback';
      fallback.setAttribute('aria-hidden', 'true');
      fallback.textContent = String(index + 1).padStart(2, '0');
      button.append(fallback);
    }

    button.addEventListener('click', () => goToGalleryMedia(modal, mediaId));
    return button;
  };

  const ensureThumbnailRail = (modal, mediaWrapper, slides) => {
    if (!mediaWrapper) return false;

    const signature = slides.map(getSlideMediaId).filter(Boolean).join('|');
    let rail = mediaWrapper.querySelector('[data-spv4-gallery-thumbnails]');

    if (slides.length < 2) {
      const changed = Boolean(rail);
      rail?.remove();
      return changed;
    }

    if (!rail) {
      rail = document.createElement('div');
      rail.className = 'spv4-qv-thumbnails';
      rail.setAttribute('data-spv4-gallery-thumbnails', '');
      rail.setAttribute('role', 'group');
      rail.setAttribute('aria-label', 'Product media thumbnails');
      mediaWrapper.append(rail);
    }

    if (rail.dataset.spv4MediaSignature === signature) return false;

    rail.replaceChildren();
    slides.forEach((slide, index) => {
      const thumbnail = buildThumbnailButton(modal, slide, index, slides.length);
      if (thumbnail) rail.append(thumbnail);
    });
    rail.dataset.spv4MediaSignature = signature;
    rail.dataset.spv4ActiveMediaId = '';
    return true;
  };

  const injectGalleryControls = (modal, productInfo) => {
    const mediaWrapper = productInfo.querySelector('.product__media-wrapper');
    const { list, slides, stage } = getGalleryParts(modal);
    if (!mediaWrapper || !list || slides.length === 0) return;

    removeNativeGalleryChrome(mediaWrapper);
    hydrateGalleryList(modal, list);
    ensureGalleryChrome(modal, stage || mediaWrapper, list, slides.length);
    const signatureChanged = ensureThumbnailRail(modal, mediaWrapper, slides);

    let activeIndex = -1;
    if (signatureChanged) activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (activeIndex < 0) activeIndex = slides.findIndex((slide) => slide.dataset.spv4Active === 'true');
    if (activeIndex < 0) activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (activeIndex < 0) activeIndex = 0;

    setGalleryIndex(modal, activeIndex, { playMedia: false });
  };

  const injectQuickViewCopy = (modal, productInfo) => {
    const productId = modal.dataset.spv4ProductId;
    if (!productId) return;

    const template = document.querySelector(`template[data-spv4-quick-copy="${productId}"]`);
    if (!template) return;

    const info = productInfo.querySelector('.product__info-container');
    if (!info || info.querySelector('[data-spv4-qv-accordions]')) return;

    const copy = template.content.cloneNode(true);
    const detailsLink = info.querySelector('.product__view-details');
    if (detailsLink) detailsLink.before(copy);
    else info.append(copy);
  };

  const positionFullDetailsCta = (productInfo) => {
    const info = productInfo.querySelector('.product__info-container');
    const detailsLink = info?.querySelector('.product__view-details');
    if (!info || !detailsLink) return;

    detailsLink.classList.add('spv4-qv-full-details');
    const accordions = info.querySelector('[data-spv4-qv-accordions]');

    if (accordions) {
      detailsLink.classList.remove('spv4-qv-full-details--standalone');
      if (detailsLink.parentElement !== accordions) accordions.append(detailsLink);
    } else {
      detailsLink.classList.add('spv4-qv-full-details--standalone');
    }
  };

  function enhanceQuickView(modal) {
    const body = modal.querySelector('[id^="QuickAddInfo-"]');
    const productInfo = body?.querySelector('product-info');
    if (!productInfo) return;

    injectGalleryControls(modal, productInfo);
    injectQuickViewCopy(modal, productInfo);
    positionFullDetailsCta(productInfo);
  }

  const hydrateQuickViewModal = (modal) => {
    if (!modal || modal.dataset.spv4ObserverReady === 'true') return;

    const body = modal.querySelector('[id^="QuickAddInfo-"]');
    if (!body) return;

    modal.dataset.spv4ObserverReady = 'true';
    let mutationRaf = 0;

    const observer = new MutationObserver(() => {
      cancelAnimationFrame(mutationRaf);
      mutationRaf = requestAnimationFrame(() => enhanceQuickView(modal));
    });

    observer.observe(body, { childList: true, subtree: true });
    modal.spv4Observer = observer;
  };

  const hydrateQuickViewModals = (scope = document) => {
    const modals = [];
    if (scope?.matches?.('.spv4-quick-view')) modals.push(scope);
    if (scope?.querySelectorAll) modals.push(...scope.querySelectorAll('.spv4-quick-view'));
    modals.forEach(hydrateQuickViewModal);
  };

  const openQuickView = async (trigger) => {
    let modal = getQuickViewModal(trigger);
    if (!modal) return;

    modal.dataset.spv4ProductId = trigger.dataset.spv4ProductId || '';
    hydrateQuickViewModal(modal);

    if (typeof modal.show !== 'function' && window.customElements) {
      await customElements.whenDefined('quick-add-modal');
      modal = getQuickViewModal(trigger);
    }

    if (!modal || typeof modal.show !== 'function') return;
    modal.show(trigger);
  };

  const handleAccordionClick = (summary) => {
    const current = summary.closest('.spv4-qv-accordion');
    const group = summary.closest('[data-spv4-qv-accordions]');
    if (!current || !group || current.open) return;

    group.querySelectorAll('.spv4-qv-accordion[open]').forEach((item) => {
      if (item !== current) item.removeAttribute('open');
    });
  };

  const handleClick = (event) => {
    const previous = event.target.closest('[data-spv4-nav-prev]');
    if (previous) {
      event.preventDefault();
      moveNavigator(previous, -1);
      return;
    }

    const next = event.target.closest('[data-spv4-nav-next]');
    if (next) {
      event.preventDefault();
      moveNavigator(next, 1);
      return;
    }

    const accordionSummary = event.target.closest('.spv4-qv-accordion > summary');
    if (accordionSummary) handleAccordionClick(accordionSummary);

    const quickView = event.target.closest(quickViewTriggerSelector);
    if (!quickView) return;

    event.preventDefault();
    event.stopPropagation();
    void openQuickView(quickView);
  };

  const handleQuickViewIntent = (event) => {
    const quickView = event.target.closest?.(quickViewTriggerSelector);
    if (quickView) prefetchQuickView(quickView);
  };

  const hydrate = (scope = document) => {
    hydrateNavigators(scope);
    hydrateQuickViewModals(scope);
  };

  document.addEventListener('click', handleClick);
  document.addEventListener('pointerover', handleQuickViewIntent, { passive: true });
  document.addEventListener('pointerdown', handleQuickViewIntent, { passive: true });
  document.addEventListener('focusin', handleQuickViewIntent);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hydrate(), { once: true });
  } else {
    hydrate();
  }

  document.addEventListener('shopify:section:load', (event) => hydrate(event.target));
})();
